import type { ConsumptionMonthlyData } from 'types'

import { useState, useEffect } from 'react'
import styled from 'styled-components'

import { classNames } from 'utils'
import copy from 'copy.json'

import Tooltip from './Tooltip'

type Props = {
  data: ConsumptionMonthlyData[]
}

const { months } = copy.common

export default function MonthlyDataGraph({ data }: Props) {
  const [hoveredItem, setHoveredItem] = useState(-1)

  useEffect(() => {
    const resetHover = () => setHoveredItem(-1)

    window.addEventListener('scroll', resetHover)

    return () => window.removeEventListener('scroll', resetHover)
  }, [])

  const biggestValue = Math.max(...data.map(entry => entry.total))

  return (
    <Wrapper onMouseLeave={() => setHoveredItem(-1)}>
      <h4>{copy.components.consumption_details.monthly_data_graph.title}</h4>

      <Bars className="barsConsumption">
        {data.map((entry, index) => {
          const sum = entry.total
          const barHeight = (sum / biggestValue) * 100
          const L1Height = Math.round(entry.zone_data.L1?.participating_percentage || 0)
          const L2Height = Math.round(entry.zone_data.L2?.participating_percentage || 0)
          const L3Height = Math.round(entry.zone_data.L3?.participating_percentage || 0)

          return (
            <Entry
              key={index}
              className={classNames(hoveredItem > -1 && hoveredItem !== index && 'dimmed')}
            >
              <BarWrapper>
                <Bar
                  className="barConsumption"
                  style={{ height: barHeight + '%' }}
                  onMouseEnter={() => setHoveredItem(index)}
                >
                  <L1 style={{ height: L1Height + '%' }} />
                  <L2 style={{ height: L2Height + '%' }} />
                  <L3 style={{ height: L3Height + '%' }} />
                </Bar>
              </BarWrapper>

              <Month>{months[index].slice(0, 3)}</Month>
            </Entry>
          )
        })}
      </Bars>

      {hoveredItem > -1 && <Tooltip hoveredItem={hoveredItem} entry={data[hoveredItem]} />}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
  max-width: 100%;

  h4 {
    font: var(--600-16);
    text-align: start;
  }

  @media (min-width: 769px) {
    padding: 0 clamp(0px, 8vw, 10%);
  }
`

const Bars = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: auto;
  overflow-y: hidden;
`

const Entry = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;

  &.dimmed {
    opacity: 0.2;
  }
`

const BarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 24px;
  height: 200px;
  overflow: hidden;
`

const Bar = styled.div`
  display: flex;
  flex-direction: column-reverse;
  border-radius: 4px;
  overflow: hidden;
`

const L1 = styled.div`
  background: var(--light-blue-consumption);
`

const L2 = styled.div`
  background: var(--blue-consumption);
`

const L3 = styled.div`
  background: var(--dark-blue-consumption);
`

const Month = styled.span`
  color: var(--gray);
  font: var(--600-12);
`
